/**
 * The MoH registers (pack `reports.json`), generated on the device for an Ethiopian month from
 * what was recorded: shown as on the paper register, printable, and exportable to CSV.
 */
import {
  ETH_MONTHS_EN,
  type Pack,
  type PatientBundle,
  type RegisterDef,
  type Value,
  ethToIso,
  registerRows,
} from '@assist/core';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { loadBundles } from '../care.js';
import { clock } from '../controller.js';
import { eth } from '../dates.js';
import { useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';

/** A cell as the paper register prints it: a tick, an Ethiopian date, or the value. */
export function cell(v: Value): string {
  if (v === true) return '✓';
  if (v === false || v === null || v === undefined) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v))
    return `${eth(v)} ${v.slice(11, 16)}`;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return eth(v);
  return String(v);
}

const csvCell = (s: string) => `"${s.replace(/"/g, '""')}"`;

export function Registers() {
  const a = useApp();
  const today = clock.todayEth();
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month > 12 ? 12 : today.month);
  const [bundles, setBundles] = useState<PatientBundle[] | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the store changes
  useEffect(() => {
    let live = true;
    void loadBundles(true).then((b) => live && setBundles(b));
    return () => {
      live = false;
    };
  }, [a.status]);
  const regs: Array<{ pack: Pack; def: RegisterDef }> = activePacks(a.packSettings).flatMap(
    ({ pack }) => (pack.registers ?? []).map((def) => ({ pack, def })),
  );
  const [id, setId] = useState<string>('');
  const chosen = regs.find((r) => `${r.pack.id}/${r.def.id}` === id) ?? regs[0];
  const from = ethToIso({ year, month, day: 1 }) ?? '';
  const to = ethToIso({ year, month, day: 30 }) ?? '';
  const rows = useMemo(
    () => (chosen && bundles ? registerRows(chosen.def, bundles, from, to) : null),
    [chosen, bundles, from, to],
  );
  if (!regs.length)
    return (
      <div class="card">
        <h3>{t.registersTitle}</h3>
        <p class="muted">{t.noPacks}</p>
      </div>
    );
  const def = chosen?.def as RegisterDef;
  const headers = def.tally ? def.tally.columns.map((c) => c.h) : def.columns.map((c) => c.h);
  const exportCsv = () => {
    if (!rows) return;
    const text = [
      headers.map(csvCell).join(','),
      ...rows.map((r) => r.map((v) => csvCell(cell(v))).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `moh_${def.id}_register_${from}_to_${to}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <section class="registers">
      <div class="card noprint">
        <h3>{t.registersTitle}</h3>
        <p class="muted">{t.registersHelp}</p>
        <div class="row3">
          <label>
            {t.registerLabel}
            <select
              value={chosen ? `${chosen.pack.id}/${chosen.def.id}` : ''}
              onChange={(e) => setId(e.currentTarget.value)}
            >
              {regs.map((r) => (
                <option key={`${r.pack.id}/${r.def.id}`} value={`${r.pack.id}/${r.def.id}`}>
                  {r.def.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.monthLabel}
            <select value={month} onChange={(e) => setMonth(Number(e.currentTarget.value))}>
              {ETH_MONTHS_EN.slice(0, 12).map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.yearLabel}
            <select value={year} onChange={(e) => setYear(Number(e.currentTarget.value))}>
              {[0, 1, 2, 3].map((d) => (
                <option key={d} value={today.year - d}>
                  {today.year - d}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div class="actions left">
          <button type="button" class="sec" onClick={() => window.print()} disabled={!rows}>
            {t.print}
          </button>
          <button type="button" class="sec" onClick={exportCsv} disabled={!rows}>
            {t.exportCsv}
          </button>
        </div>
      </div>
      <div class="card" data-register={def.id}>
        <h3>{def.heading}</h3>
        <p class="muted">
          {[
            a.session?.user.facilityName,
            `${ETH_MONTHS_EN[month - 1]} ${year}`,
            rows ? t.rowsN(rows.length) : t.computing,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        {rows && !rows.length ? <p class="empty">{t.nothingInPeriod}</p> : null}
        {rows?.length ? (
          <div class="regwrap">
            <table class="reg">
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: register rows have no identity
                  <tr key={i}>
                    {r.map((v, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                      <td key={j}>{cell(v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </section>
  );
}
